import React from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import AssignmentSummary from '../assignment-summary';
import FormActions from '../form-actions';
import { sortByLesson } from '../../utils';
import { Course, SelectedAssignment } from '../../types';

// react-bootstrap bundles its own @types/react, so Alert alone fails to typecheck as JSX.
const AlertBox = Alert as unknown as React.FC<{
  className?: string;
  variant?: string;
  children?: React.ReactNode;
}>;

interface ConfirmPageProps {
  currentPage: number;
  assignmentSetName?: string;
  assignmentSetId: unknown;
  assignments: SelectedAssignment[];
  error?: string;
  courses: Course[];
  selectedCourseIds: number[];
  selectedCourseNames: string[];
  bonusCourses: Course[];
  bonusCourseIds: number[];
  onBack: () => void;
  onConfirm: () => void;
}

const ConfirmPage = ({
  currentPage, assignmentSetName, assignmentSetId, assignments, error,
  courses, selectedCourseIds, selectedCourseNames,
  bonusCourses, bonusCourseIds, onBack, onConfirm,
}: ConfirmPageProps) => {
  // selectedCourseNames and selectedCourseIds are parallel arrays kept in step by onCourseToggle.
  const renderCoursesAssignedTo = () => selectedCourseNames.map((courseName, index) => {
    const course = courses.find((current) => current.id === selectedCourseIds[index]);
    if (course && course.assignment_set && course.assignment_set_id !== assignmentSetId) {
      const warning = `Important: ${courseName} is currently using ${course.assignment_set.name}. `
        + `Assigning ${assignmentSetName} will replace the current assignment set.`;
      return (
        <li key={course.id}>
          <div className="assigned-to">
            <div className="assigned-courses"><b>{courseName}</b></div>
            <div className="assign-warning">
              <p>{warning}</p>
            </div>
          </div>
        </li>
      );
    }
    return (
      <li key={course ? course.id : courseName}>
        <b>{courseName}</b>
      </li>
    );
  });

  const renderBonusCourses = () => {
    if (bonusCourses.length === 0) return (<li>No courses selected.</li>);
    return bonusCourses.map((course) => (
      <li key={course.id}>
        <b>{course.name}</b>
        {bonusCourseIds.includes(course.id) ? ' - on' : ' - off'}
      </li>
    ));
  };

  return (
    <>
      <Row className="mt-5 mb-5">
        <Col sm={12} lg={6}>
          <b>{`${currentPage}. Confirmation`}</b>
          <div className="d-flex mt-4">
            <b>Name:</b>
            <p className="ml-3">{assignmentSetName}</p>
          </div>
          {assignments.length > 0 && (
            <AssignmentSummary
              assignmentInfo={sortByLesson(assignments)}
            />
          )}
        </Col>
      </Row>
      {error && (
        <AlertBox className="text-center mt-3 p-1" variant="danger">
          <small>{`${error}.`}</small>
        </AlertBox>
      )}
      <Row>
        <div className="mt-4">
          <b>Assigned To:</b>
          <ul>
            {renderCoursesAssignedTo()}
          </ul>
        </div>
      </Row>
      <Row>
        <div className="mt-4">
          <b>Persistence with Bonus:</b>
          <ul>
            {renderBonusCourses()}
          </ul>
        </div>
      </Row>
      <FormActions onBack={onBack} onSave={onConfirm} saveText="Confirm and Assign" />
    </>
  );
};

export default ConfirmPage;
